import { notification } from 'antd'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'
import { useMemo } from 'react'

import { QUERY_KEYS } from '@api'
import { ConfigurationProperties, ConfigurationPropertiesEnum } from '@types'

import { ConfigurationPropertiesApi } from './api'

const useConfigurationProperties = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const onError = () => {
    notification.error({
      message: t('error_retrieving_information'),
    })
  }

  const { data, isLoading } = useQuery(
    [QUERY_KEYS.CONFIGURATION_PROPERTIES],
    ConfigurationPropertiesApi.getConfigurationProperties,
    {
      staleTime: Infinity,
      onError,
    },
  )

  const allocationData =
    data &&
    data.find(
      (setting: ConfigurationProperties) =>
        setting.key === ConfigurationPropertiesEnum.ALLOCATION_FREQUENCY,
    )
  const allocationMinutes = allocationData ? parseInt(allocationData.value, 10) : undefined
  const allocationId = allocationData?.id
  const deleteTicketsData =
    data &&
    data.find(
      (setting: ConfigurationProperties) =>
        setting.key === ConfigurationPropertiesEnum.PRICE_PERCENTAGE,
    )
  const deleteTicketsDays = deleteTicketsData ? parseInt(deleteTicketsData.value, 10) : undefined
  const deleteTicketsId = deleteTicketsData?.id

  const assignmentTicketsData =
    data &&
    data.find(
      (setting: ConfigurationProperties) =>
        setting.key === ConfigurationPropertiesEnum.AUTOMATIC_ASSIGNMENT,
    )
  const assignmentTickets = assignmentTicketsData ? String(assignmentTicketsData.value) : 0
  const assignmentTicketsId = assignmentTicketsData?.id

  const preAuthTicketsData =
    data &&
    data.find(
      (setting: ConfigurationProperties) =>
        setting.key === ConfigurationPropertiesEnum.SUPPLIER_CAN_CREATE_AUTOMATIC_TICKETS,
    )

  const preAuthTickets = preAuthTicketsData ? String(preAuthTicketsData.value) : 0
  const preAuthTicketsId = preAuthTicketsData?.id

  const carrierAvailabilityIsEnabled = useMemo(
    () =>
      data?.find(
        (setting: ConfigurationProperties) =>
          setting.key === ConfigurationPropertiesEnum.USE_CARRIER_AVAILABILITY,
      )?.value === 'true',
    [data],
  )

  const requireTruckAndTrailer = useMemo(
    () =>
      data?.find(
        (setting: ConfigurationProperties) =>
          setting.key === ConfigurationPropertiesEnum.REQUIRE_TRUCK_AND_TRAILER,
      )?.value === 'true',
    [data],
  )

  const requireDriver = useMemo(
    () =>
      data?.find(
        (setting: ConfigurationProperties) =>
          setting.key === ConfigurationPropertiesEnum.REQUIRE_DRIVER,
      )?.value === 'true',
    [data],
  )

  const maxAllowedDaysForSupplierRequest = useMemo(() => {
    const cfg = data?.find(
      (setting: ConfigurationProperties) =>
        setting.key === ConfigurationPropertiesEnum.MAX_ALLOWED_DAYS_FOR_SUPPLIER_REQUEST,
    )

    return !!cfg?.value ? parseInt(cfg.value) : 0
  }, [data])

  const onMutationSuccessTime = () => {
    notification.success({
      message: t('admin_router.admin_dashboard.settings.successTime'),
    })

    queryClient.invalidateQueries(QUERY_KEYS.CONFIGURATION_PROPERTIES)
  }
  const onMutationSuccessPercentaje = () => {
    notification.success({
      message: t('admin_router.admin_dashboard.settings.successPercentaje'),
    })

    queryClient.invalidateQueries(QUERY_KEYS.CONFIGURATION_PROPERTIES)
  }
  const onMutationSuccessAssignmentTickets = () => {
    notification.success({
      message: t('admin_router.admin_dashboard.settings.success_assignment_tickets'),
    })

    queryClient.invalidateQueries(QUERY_KEYS.CONFIGURATION_PROPERTIES)
  }
  const onMutationSuccessPreauthTickets = () => {
    notification.success({
      message: t('admin_router.admin_dashboard.settings.success_preauth_tickets'),
    })

    queryClient.invalidateQueries(QUERY_KEYS.CONFIGURATION_PROPERTIES)
  }
  const onMutationError = (err: AxiosError) => {
    const errorResponse = err.response?.data.error
    notification.error({
      message: t('admin_router.admin_dashboard.settings.error'),
    })
    if (errorResponse.message.toLowerCase().includes('allocation frequency')) {
      notification.error({
        message: t('admin_router.admin_dashboard.settings.error_minutes'),
      })
    }
    queryClient.invalidateQueries(QUERY_KEYS.CONFIGURATION_PROPERTIES)
  }

  const { mutate: updateConfigPropsTime, isLoading: isConfigLoadingTime } = useMutation(
    ConfigurationPropertiesApi.updateConfigurationProperties,
    {
      onSuccess: onMutationSuccessTime,
      onError: onMutationError,
    },
  )
  const { mutate: updateConfigPropsPercentaje, isLoading: isConfigLoadingPercentaje } = useMutation(
    ConfigurationPropertiesApi.updateConfigurationProperties,
    {
      onSuccess: onMutationSuccessPercentaje,
      onError: onMutationError,
    },
  )
  const {
    mutate: updateConfigPropsAssignmentTickets,
    isLoading: isConfigLoadingAssignmentTickets,
  } = useMutation(ConfigurationPropertiesApi.updateConfigurationProperties, {
    onSuccess: onMutationSuccessAssignmentTickets,
    onError: onMutationError,
  })
  const { mutate: updatePreAuthTickets, isLoading: isConfigLoadingUpdatePreAuthTickets } =
    useMutation(ConfigurationPropertiesApi.updateConfigurationProperties, {
      onSuccess: onMutationSuccessPreauthTickets,
      onError: onMutationError,
    })

  return {
    settings: data,
    isLoading,
    allocationMinutes,
    allocationId,
    deleteTicketsDays,
    deleteTicketsId,
    assignmentTicketsId,
    assignmentTickets,
    preAuthTicketsId,
    preAuthTickets,
    carrierAvailabilityIsEnabled,
    requireTruckAndTrailer,
    assignmentTicketsData,
    updateConfigPropsAssignmentTickets,
    updateConfigPropsTime,
    updateConfigPropsPercentaje,
    isConfigLoadingAssignmentTickets,
    isConfigLoadingTime,
    isConfigLoadingPercentaje,
    updatePreAuthTickets,
    isConfigLoadingUpdatePreAuthTickets,
    maxAllowedDaysForSupplierRequest,
    requireDriver,
  }
}

export { useConfigurationProperties }
